/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useEffect } from 'react';
import SpellLevelSelector from './SpellLevelSelector';
import Spell from 'model/stats/Spell';
import SpellService from 'services/SpellService';
import ClassService from 'services/ClassService';
import { ClassDetails } from 'model/character/ClassDetails';

interface SpellbookSelectorProps {
  classDetails: ClassDetails
  spellsBySpellLevelByClass: Map<string, Map<number, Spell[]>>;
  onSpellbookSpellsChanged: (level: number, spells: Spell[]) => void;
  openLevel: number; // -1 means nothing open
  setOpenLevel: (level: number) => void;
  onClickOutside: (event: MouseEvent) => void;
}

const SpellbookSelector: React.FC<SpellbookSelectorProps> = ({classDetails, spellsBySpellLevelByClass, onSpellbookSpellsChanged, openLevel, setOpenLevel, onClickOutside}) => {

  useEffect(() => {
    // Dependencies can be added if they affect the spells data
  }, []); 

  if (classDetails?.classInfo?.name?.toLowerCase() !== 'wizard') {
    return null;
  }

  const spellsKnowableArrayBySpellLevelMap = spellsBySpellLevelByClass.get(classDetails.classInfo.name.toLowerCase() || '') ||
                                             spellsBySpellLevelByClass.get(classDetails.archetype?.name.toLowerCase() || '');

  if (!spellsKnowableArrayBySpellLevelMap) {
    return null;
  }

  let totalSelected = 0;
  let totalSelectable = 0;
  const spellsKnowableArrayBySpellLevelArray: Spell[][] = [];
  for (let spellLevel = 0; spellLevel <= 9; spellLevel++) {
    const spells = [...(spellsKnowableArrayBySpellLevelMap.get(spellLevel) || [])];
    spellsKnowableArrayBySpellLevelArray[spellLevel] = spells.sort((a, b) => (a.name < b.name ? -1 : (a.name > b.name ? 1 : 0)));
    if (spellLevel > 0) {
      totalSelected += classDetails.spellbookSpellsArrayBySpellLevel[spellLevel]?.filter(o => o).length || 0;
      totalSelectable += SpellService.spellbookSpellsKnowableByWizardLevel[classDetails.classLevel][spellLevel];
    }
  }

  const totalUnselected = totalSelectable - totalSelected;
  const spellsAvailable = [];
  let extra = 0;
  for (let spellLevel = 9; spellLevel > 0; spellLevel--) {
    const selectedCount = classDetails.spellbookSpellsArrayBySpellLevel[spellLevel]?.filter(o => o).length || 0;
    const maxAllowed = SpellService.spellbookSpellsKnowableByWizardLevel[classDetails.classLevel][spellLevel];
    extra += maxAllowed
    spellsAvailable[spellLevel] = Math.min(extra, (totalUnselected + selectedCount));
    extra -= selectedCount;
  }

  const selectorsSpellbook: JSX.Element[] = [];
  selectorsSpellbook.push();

  // create the Spellbook element
  for (let spellLevel = 1; spellLevel <= 9; spellLevel++) {
    const selectedCount = classDetails.spellbookSpellsArrayBySpellLevel[spellLevel]?.filter(o => o).length || 0;
    const maxAllowed = spellsAvailable[spellLevel];
    if (maxAllowed > 0) {
      const notes = `(${selectedCount} / ${maxAllowed})`;
      selectorsSpellbook.push(
        <tr key={spellLevel}>
          <td>
            {ClassService.getLevelString(spellLevel)}
          </td>
          <td>
            <SpellLevelSelector notes={notes} maxSpellCount={maxAllowed}
                                spellsKnowableArray={spellsKnowableArrayBySpellLevelArray[spellLevel] || []}
                                spellsSelectedArray={classDetails.spellbookSpellsArrayBySpellLevel[spellLevel] || []}
                                onSpellsSelectedChanged={onSpellbookSpellsChanged}
                                level={spellLevel} openLevel={openLevel} setOpenLevel={setOpenLevel}
                                showGrouping={true} onClickOutside={onClickOutside}
            />
          </td>
        </tr>
      );
    }
  }

  return  <div>
            <h3>Spellbook:</h3>
            <table className="markdown-table" id="SpellbookSelector">
              <thead>
                <tr>
                  <th>Level</th>
                  <th>Spells</th>
                </tr>
              </thead>
              <tbody>
               {selectorsSpellbook}
              </tbody>
            </table>
          </div>
};

export default SpellbookSelector;
